import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR1030_R01Store } from "./Store_PUR1030_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR1030_R01HeaderDto, PUR1030_R01HeaderDto } from "./Dto_PUR1030_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1030_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR1030_R01HeaderDto> | null>;
	defaultPUR1030_R01HeaderRow: PUR1030_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_PUR1030_R01 = (props: Props) => {

	const { gridRef, defaultPUR1030_R01HeaderRow, handleCreateClick } = props;

	const { PUR1030_R01List, 
		selectedPUR1030_R01, setSelectedPUR1030_R01, initSelectedPUR1030_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = usePUR1030_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'purchaseOrderNo',
		gridData: PUR1030_R01List,
		defaultGridData: defaultPUR1030_R01HeaderRow,
		selectedRow: selectedPUR1030_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		purchaseOrderNo: '지시번호',
		compNm: '사업장',
		orderDt: '발주일자',
		customNm: '상호',
		orderAmt: '매출금액',
		orderStatus: '발주상태',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'purchaseOrderNo')(params), []);

	const baseColumnDefs: ColDef<PUR1030_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '지시번호', colId: 'purchaseOrderNo', field: 'purchaseOrderNo', width: 130,
			cellStyle: (p) => getCellStyle(p, 'purchaseOrderNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 119,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '발주일자', colId: 'orderDt', field: 'orderDt', width: 107,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: PUR1030_R01HeaderDto }) =>
				p.data?.orderDt ? formatDate(new Date(p.data.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '발주금액', colId: 'orderAmt', field: 'orderAmt', width: 118,
			cellStyle: (p) => getCellStyle(p, 'orderAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '발주상태', colId: 'orderStatus', field: 'orderStatus', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderStatus'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => {
				if (params.node?.rowPinned === 'bottom') return '';
				if (params.value === 'E') return '입고완료';
				if (params.value === 'C') return '취소';
				if (params.value === 'P') return '입고진행중';
				if (params.value === 'N') return '입고대기';
				return '';
			},
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR1030_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PUR1030_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	

	
	const handleRowClick = useCallback((e: RowClickedEvent<PUR1030_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedPUR1030_R01?.purchaseOrderNo === rowData.purchaseOrderNo;
		
		if (isSelected) {
			setSelectedPUR1030_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedPUR1030_R01(rowData);
		}
	}, [selectedPUR1030_R01, setSelectedPUR1030_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		purchaseOrderNo: PUR1030_R01List.length.toLocaleString() + "건",
		orderAmt: PUR1030_R01List.reduce((sum, item) => (sum + item.orderAmt), 0).toLocaleString(),
		editable: false,
	}]), [PUR1030_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PUR1030_R01List, selectedPUR1030_R01]);

	return (
		
		<GridContextWrapper<PUR1030_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPUR1030_R01}
			defaultRowData={defaultPUR1030_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PUR1030_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<PUR1030_R01HeaderDto>({
					idKey: 'purchaseOrderNo',
					selectedList: selectedPUR1030_R01 ? [selectedPUR1030_R01.purchaseOrderNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
